const IMAGE_EXT = /\.(jpe?g|png|gif|webp|avif|tiff?|heic|heif|bmp|svg)$/i;
const VIDEO_EXT = /\.(mp4|mov|m4v|webm|mkv|avi)$/i;
let sequence = 0;
const REVOKE_DELAY_MS = 1000;
export function kindOf(file) {
    if (file.type.startsWith('image/') || IMAGE_EXT.test(file.name))
        return 'image';
    if (file.type.startsWith('video/') || VIDEO_EXT.test(file.name))
        return 'video';
    return 'file';
}
function accepts(file, accept) {
    const name = file.name.toLowerCase();
    return accept.some((entry) => {
        const rule = entry.toLowerCase();
        if (rule.startsWith('.'))
            return name.endsWith(rule);
        if (rule.endsWith('/*'))
            return file.type.toLowerCase().startsWith(rule.slice(0, -1));
        if (rule.endsWith('/'))
            return file.type.toLowerCase().startsWith(rule);
        return file.type.toLowerCase() === rule;
    });
}
function sizeLimit(maxSizeMB, kind) {
    return typeof maxSizeMB === 'number' ? maxSizeMB : (maxSizeMB?.[kind] ?? 500);
}
function preview(file, kind) {
    return kind === 'file' ? '' : URL.createObjectURL(file);
}
async function customRejection(file, validate) {
    try {
        const message = await validate(file);
        return message ? { file, reason: 'custom', message } : undefined;
    }
    catch (error) {
        return { file, reason: 'custom', message: error instanceof Error ? error.message : String(error) };
    }
}
export function createUploadSession(initialProps = {}) {
    let props = initialProps;
    let items = [];
    let disposed = false;
    let started = initialProps.autoUpload !== false;
    let manuallyStarted = false;
    let settledKey = '';
    let pendingAdds = 0;
    const listeners = new Set();
    const controllers = new Map();
    const running = new Set();
    let scheduling = false;
    let reschedule = false;
    const notify = () => {
        for (const listener of listeners)
            listener();
    };
    const update = (fn) => {
        items = fn(items);
        notify();
        schedule();
    };
    const current = (id) => items.find((item) => item.id === id);
    // Deferred: a chip or tile mounted in the same tick as clear()/remove() may still
    // be fetching its preview, and revoking now fails that request.
    const revoke = (item) => {
        if (item.previewUrl && !item.existing)
            setTimeout(() => URL.revokeObjectURL(item.previewUrl), REVOKE_DELAY_MS);
    };
    const completion = () => {
        if (!items.length || !items.every((item) => item.phase === 'done'))
            return;
        const key = items.map((item) => item.id).join('|');
        if (key !== settledKey) {
            settledKey = key;
            props.onAllComplete?.(items);
        }
    };
    async function run(item) {
        const controller = new AbortController();
        controllers.set(item.id, controller);
        const attempt = item.attempt + 1;
        update((all) => all.map((i) => (i.id === item.id ? { ...i, phase: 'transferring', attempt } : i)));
        let done;
        try {
            props.onUploadStart?.(current(item.id) ?? item);
            if (!props.uploader)
                throw new Error('MediaDrop uploader is not configured');
            const result = await props.uploader(item.file, {
                signal: controller.signal,
                attempt,
                previousResult: item.previousResult,
                onProgress: (fraction) => {
                    if (controller.signal.aborted || !current(item.id))
                        return;
                    const progress = Math.max(current(item.id).progress, Math.max(0, Math.min(1, fraction)));
                    update((all) => all.map((i) => (i.id === item.id ? { ...i, progress } : i)));
                    props.onProgress?.(current(item.id), progress);
                },
                onProcessing: () => {
                    if (!controller.signal.aborted && current(item.id))
                        update((all) => all.map((i) => (i.id === item.id ? { ...i, phase: 'processing', progress: 1 } : i)));
                },
                setPreview: (blob) => {
                    const live = current(item.id);
                    if (controller.signal.aborted || !live)
                        return;
                    revoke(live);
                    const previewUrl = URL.createObjectURL(blob);
                    update((all) => all.map((i) => (i.id === item.id ? { ...i, previewUrl } : i)));
                },
            });
            if (controller.signal.aborted || !current(item.id))
                return;
            update((all) => all.map((i) => (i.id === item.id ? { ...i, phase: 'done', result, previousResult: result, progress: 1 } : i)));
            done = current(item.id);
        }
        catch (cause) {
            if (controller.signal.aborted || !current(item.id))
                return;
            const error = cause instanceof Error ? cause : new Error(String(cause));
            const previousResult = 'previousResult' in error ? error.previousResult : item.previousResult;
            update((all) => all.map((i) => (i.id === item.id ? { ...i, phase: 'error', error: error.message, previousResult } : i)));
            props.onItemError?.(current(item.id), error);
        }
        finally {
            controllers.delete(item.id);
            running.delete(item.id);
            schedule();
        }
        // Outside the try: a throwing caller callback must not turn a finished upload into an error.
        if (!done)
            return;
        try {
            props.onItemComplete?.(done);
        }
        finally {
            completion();
        }
    }
    function schedule() {
        if (scheduling) {
            reschedule = true;
            return;
        }
        if (disposed || !started || props.disabled)
            return;
        scheduling = true;
        try {
            let slots = Math.max(0, (props.concurrency ?? 3) - running.size);
            for (const item of items) {
                if (!slots)
                    break;
                if (item.phase !== 'queued' || running.has(item.id))
                    continue;
                running.add(item.id);
                slots--;
                void run(item);
            }
        }
        finally {
            scheduling = false;
            if (reschedule) {
                reschedule = false;
                queueMicrotask(schedule);
            }
        }
    }
    return {
        getSnapshot: () => items,
        subscribe: (listener) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        configure(next) {
            props = next;
            started = next.autoUpload !== false || manuallyStarted;
            schedule();
        },
        async addFiles(files) {
            if (!files.length || disposed || props.disabled)
                return;
            const accept = props.accept ?? ['image/', 'video/'];
            const multiple = props.multiple ?? true;
            const maxFiles = multiple ? (props.maxFiles ?? 20) : 1;
            const accepted = [];
            const rejected = [];
            for (const file of files) {
                const kind = kindOf(file);
                const max = sizeLimit(props.maxSizeMB, kind);
                let rejection;
                if (!accepts(file, accept))
                    rejection = { file, reason: 'type', message: 'unsupported type' };
                else if (file.size > max * 1048576)
                    rejection = { file, reason: 'size', message: `over ${max} MB`, limit: max };
                else if ((multiple ? items.length : 0) + pendingAdds >= maxFiles)
                    rejection = { file, reason: 'count', message: `limit is ${maxFiles}`, limit: maxFiles };
                else {
                    pendingAdds++;
                    if (props.validate)
                        rejection = await customRejection(file, props.validate);
                }
                if (rejection) {
                    if (rejection.reason === 'custom')
                        pendingAdds--;
                    rejected.push(rejection);
                }
                else
                    accepted.push(file);
            }
            if (disposed || props.disabled) {
                pendingAdds -= accepted.length;
                return;
            }
            if (accepted.length) {
                // Validation must succeed before replacing a saved single-image value.
                if (!multiple && items.length)
                    this.clear();
                update((all) => [
                    ...all,
                    ...accepted.map((file) => ({
                        id: `mdz-${++sequence}`,
                        file,
                        kind: kindOf(file),
                        previewUrl: preview(file, kindOf(file)),
                        progress: 0,
                        phase: 'queued',
                        attempt: 0,
                    })),
                ]);
                pendingAdds -= accepted.length;
                props.onFilesAdded?.(accepted);
            }
            if (rejected.length)
                props.onRejected?.(rejected);
        },
        addExisting(existing) {
            const known = new Set(items.map((i) => i.id));
            const next = existing
                .filter((i) => !known.has(i.id))
                .map((entry) => ({
                id: entry.id,
                file: new File([], entry.name ?? entry.url.split('/').pop() ?? 'Existing media'),
                kind: entry.kind,
                previewUrl: entry.url,
                progress: 1,
                phase: 'done',
                result: entry.url,
                previousResult: entry.url,
                attempt: 0,
                existing: true,
            }));
            if (next.length)
                update((all) => [...all, ...next]);
        },
        start() {
            manuallyStarted = true;
            started = true;
            schedule();
        },
        remove(item) {
            const wasDone = current(item.id)?.phase === 'done';
            controllers.get(item.id)?.abort();
            revoke(item);
            update((all) => all.filter((i) => i.id !== item.id));
            props.onRemove?.(item);
            // Dropping the last failed or pending item can leave the rest all done.
            if (!wasDone)
                completion();
        },
        retry(item) {
            if (item.phase !== 'error')
                return;
            update((all) => all.map((i) => (i.id === item.id ? { ...i, phase: 'queued', error: undefined, progress: 0 } : i)));
            props.onRetry?.(item);
        },
        clear() {
            controllers.forEach((c) => c.abort());
            items.forEach(revoke);
            update(() => []);
            settledKey = '';
        },
        dispose() {
            disposed = true;
            controllers.forEach((c) => c.abort());
            items.forEach(revoke);
            items = [];
            listeners.clear();
        },
        setDims(id, dims) {
            update((all) => all.map((i) => (i.id === id ? { ...i, dims } : i)));
        },
    };
}
const ACTIVE = ['queued', 'transferring', 'processing'];
/**
 * Dispose once nothing is queued or in flight. For owners that unmount while their
 * uploads should keep going (a dialog or empty state swapped out mid-batch).
 */
export function disposeWhenSettled(session) {
    const settled = () => !session.getSnapshot().some((item) => ACTIVE.includes(item.phase));
    if (settled()) {
        session.dispose();
        return;
    }
    const unsubscribe = session.subscribe(() => {
        if (!settled())
            return;
        unsubscribe();
        // Deferred: run() still delivers onItemComplete/onAllComplete for the last item.
        queueMicrotask(() => session.dispose());
    });
}
//# sourceMappingURL=session.js.map